import React, { useState, useEffect, useRef, useCallback } from 'react';
import classnames from 'classnames';
import debounce from 'lodash/debounce';
import { apiHolder, apis } from 'api';
import SiriusContact from '@web-common/components/UI/SiriusContact/siriusContact';
import { filterContactListByYunxin } from '../../utils/im_team_util';
import { data2Tree, data2Leaf, updatePersonTreeData, updateOrgTreeData, getPersonContact, getOrgs, getContact, StaticNodeKey, } from '@web-common/utils/contact_util';
import ContactItem from '../ContactItem/contactItem';
import styles from './contact.module.scss';
import { getIn18Text } from 'api';
const contactApi = apiHolder.api.requireLogicalApi(apis.contactApiImpl);
const LIST_ITEM_HEIGHT = 28;
const originPersonData = [
    {
        key: StaticNodeKey.PERSON,
        title: getIn18Text('GERENTONGXUNLU'),
        children: [{ key: StaticNodeKey.PERSON_ALL, isLeaf: false, title: getIn18Text('SUOYOULIANXIREN') }],
    },
];
const originOrgData = [
    {
        key: StaticNodeKey.CORP,
        title: getIn18Text('QIYETONGXUNLU'),
        children: [],
    },
];
let observerID;
export const Checkbox = props => {
    const { checked, style, disabled } = props;
    return (React.createElement("span", { className: classnames([styles.checkbox], {
            [styles.checkboxChecked]: checked,
            [styles.checkboxDisabled]: disabled,
        }), style: Object.assign({}, style) },
        React.createElement("span", { className: styles.checkboxInner })));
};
const TreeNode = props => {
    const { node, chooseMember, chosen, disabled } = props;
    const [checked, setChecked] = useState(false);
    useEffect(() => {
        setChecked(chosen);
    }, [chosen]);
    if (node.isLeaf) {
        const onNodeClick = () => {
            if (!disabled) {
                setChecked(chooseMember(node.value));
            }
        };
        const checkboxStyle = {
            position: 'absolute',
            left: '16px',
            top: '8px',
        };
        return (React.createElement("div", { className: styles.treeNode, onClick: onNodeClick },
            React.createElement("span", null, node.title),
            React.createElement(Checkbox, { checked: checked, disabled: disabled, style: checkboxStyle })));
    }
    return (React.createElement("div", { className: styles.treeNode },
        React.createElement("span", null, node.title)));
};
const toContactModel = (item) => ({
    contact: {
        id: item.id,
        contactName: item.contactName,
        accountName: item.accountName,
        avatar: item.avatar || '',
        avatarPendant: item.avatarPendant || '',
        position: item.position,
        hitQuery: item.hitQuery,
        visibleCode: item.visibleCode,
        accountType: item.accountType,
        type: item.type,
    },
    contactInfo: [
        {
            contactItemType: 'EMAIL',
            contactItemVal: item.accountName,
        },
        {
            contactItemType: 'yunxin',
            contactItemVal: item.yxId,
            contactItemRefer: item.yxToken,
        },
    ],
});
const Contact = props => {
    const { chooseMember, chosenMembers = [], disabledMembers = [] } = props;
    const [personData, setPersonData] = useState(originPersonData);
    const [orgData, setOrgData] = useState(originOrgData);
    const [searchValue, setSearchValue] = useState('');
    const [searchContactList, setSearchContactList] = useState([]);
    const [selectedPersonKeys, setSelectedPersonKeys] = useState([]);
    const [selectedOrgKeys, setSelectedOrgKeys] = useState([]);
    const refSiriusContact = useRef(null);
    const displayContactList = searchContactList;
    useEffect(() => {
        const members = [].concat(chosenMembers, disabledMembers);
        const keys = members.map(member => member.contact.id);
        setSelectedPersonKeys(keys);
        setSelectedOrgKeys(keys);
    }, [chosenMembers, disabledMembers]);
    const throttleSearch = useCallback(debounce((value) => {
        contactApi.doSearchServerYxContact(value, enrichedContacts => {
            setSearchContactList(enrichedContacts);
        });
    }, 300), []);
    const onLoadData = (node) => {
        const { key } = node;
        return new Promise(resolve => {
            if (String(key) === StaticNodeKey.CORP) {
                resolve();
                return;
            }
            getContact(String(key)).then(list => {
                const filteredList = filterContactListByYunxin(list);
                setOrgData(origin => updateOrgTreeData(origin, key, filteredList.map(item => data2Leaf(item, 'yunxin'))));
                resolve();
            });
        });
    };
    const onSearch = (keyword) => {
        setSearchValue(keyword);
        throttleSearch(keyword);
        if (!keyword) {
            setSearchContactList([]);
        }
    };
    const renderTitle = node => {
        var _a;
        const key = contactApi.findContactInfoVal((_a = node.value) === null || _a === void 0 ? void 0 : _a.contactInfo, 'EMAIL');
        const chosen = chosenMembers.concat(disabledMembers).some(member => {
            return contactApi.findContactInfoVal(member.contactInfo, 'EMAIL') === key;
        });
        const disabled = disabledMembers.some(member => contactApi.findContactInfoVal(member.contactInfo, 'EMAIL') === key);
        return React.createElement(TreeNode, { node: node, chooseMember: chooseMember, chosen: chosen, disabled: disabled });
    };
    const refresh = () => {
        var _a;
        (_a = refSiriusContact.current) === null || _a === void 0 ? void 0 : _a.resetContactTree();
        getOrgs({ isIM: true }).then(orgData => {
            setOrgData(originOrgData.map(node => (Object.assign(Object.assign({}, node), { key: orgData.org.id, children: orgData.children.map(data2Tree) }))));
        });
        getPersonContact({ isIM: true }).then(list => {
            const filteredList = filterContactListByYunxin(list);
            setPersonData(updatePersonTreeData(originPersonData, filteredList.map(item => data2Leaf(item, 'yunxin'))));
        });
    };
    useEffect(() => {
        refresh();
        if (observerID !== undefined) {
            apiHolder.api.getEventApi().unregisterSysEventObserver('contactNotify', observerID);
        }
        observerID = apiHolder.api.getEventApi().registerSysEventObserver('contactNotify', {
            func: diff => {
                if (diff && diff.eventData.hasDiff) {
                    refresh();
                }
            },
        });
        return () => {
            apiHolder.api.getEventApi().unregisterSysEventObserver('contactNotify', observerID);
        };
    }, []);
    const searchListItemRender = ({ index, key, style }) => {
        if (!displayContactList || !displayContactList[index]) {
            return null;
        }
        const item = displayContactList[index];
        const chosen = chosenMembers.concat(disabledMembers).some(member => {
            return contactApi.findContactInfoVal(member.contactInfo, 'EMAIL') === item.accountName;
        });
        const disabled = disabledMembers.some(member => {
            return contactApi.findContactInfoVal(member.contactInfo, 'EMAIL') === item.accountName;
        });
        return (React.createElement("div", { key: key, style: style },
            React.createElement(ContactItem, { item: item, search: searchValue, chooseMember: member => chooseMember(toContactModel(member)), chosen: chosen, disabled: disabled })));
    };
    return (React.createElement(SiriusContact, { ref: refSiriusContact, onSearch: onSearch, searchListCount: (displayContactList === null || displayContactList === void 0 ? void 0 : displayContactList.length) || 0, searchListItemRender: searchListItemRender, searchListItemHeight: LIST_ITEM_HEIGHT, renderPersonTitle: renderTitle, renderOrgTitle: renderTitle, onLoadData: onLoadData, personData: personData, orgData: orgData, selectedPersonKeys: selectedPersonKeys, selectedOrgKeys: selectedOrgKeys, containerClassName: styles.contactTreeContainer }));
};
export default Contact;
